import { Box, MenuItem, TextField, useMediaQuery } from '@mui/material';
import React, { useCallback, useState } from 'react';

import CountBadge from '../CountBadge';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import ErrorOverlay from '../ErrorOverlay';
import Locale from '../Locale';
import LocalizedTooltip from '../LocalizedTooltip';
import PropTypes from 'prop-types';
import Tab from '../Tab';
import TabPanel from '../TabPanel';
import Tabs from '../Tabs';
import castArray from 'lodash/castArray';
import { makeStyles } from 'tss-react/mui';
import { styled } from '@mui/material/styles';
import styles from './DynamicTabbed.styles';
import useBoundCallback from '../../helpers/Hooks/useBoundCallback';
import { useHistory } from 'react-router';
import { useTheme } from '@mui/material/styles';

const useStyles = makeStyles()(styles);

const VBox = styled(Box)(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
}));

const getErrorsForTab = (tabsWithErrors, tab) => {
    const errors = castArray(
        tabsWithErrors && tabsWithErrors.length > 0
            ? tabsWithErrors.filter(err => castArray(err)[0] === tab)
            : []
    ).pop();

    return errors && castArray(errors.slice(-1));
};

const DynamicTabbed = props => {
    const {
        children,
        name,
        tab: controlledTab,
        initialTab,
        onChange,
        title,
        error,
        errorTabs,
        shouldAutoUpdateUrl,
        ContentWrap,
        slots,
        slotProps,
        ...remain
    } = props;
    const history = useHistory();
    const { classes, cx } = useStyles(undefined, {
        props,
    });
    let { orientation } = props;
    const [uncontrolledTab, setTab] = useState(controlledTab || initialTab);
    const tab = controlledTab || uncontrolledTab;
    const ErrorOverlayComponent = slots?.errorOverlay || ErrorOverlay;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isVerticalMobile = isMobile && orientation === 'vertical';
    const _getErrorsForTab = useBoundCallback(getErrorsForTab, [errorTabs]);
    const handleChange = useCallback(
        (event, newValue) => {
            // If tab === -1, the tab you changed to does not exist
            if (newValue === -1) {
                // Since we might need to overwrite the query string
                onChange && onChange(tab);
                return;
            }
            !controlledTab && setTab(newValue);
            onChange && onChange(newValue) && event.preventDefault();
            if (shouldAutoUpdateUrl && isVerticalMobile) {
                const selectedTabComponent = children.find(child => child.props?.tab === newValue);
                const selectedTabProps = selectedTabComponent?.props.TabProps;
                if (selectedTabProps?.to) {
                    history.push(selectedTabProps.to);
                }
            }
        },
        [controlledTab, isVerticalMobile, onChange, shouldAutoUpdateUrl, tab]
    );

    return (
        <div
            className={cx(
                classes.root,
                orientation === 'vertical' && !isMobile ? classes.vertical : classes.horizontal,
                isMobile ? classes.mobile : undefined
            )}
        >
            {isVerticalMobile ? (
                <TextField
                    className={classes.mobileTabs}
                    select
                    label={title ? <Locale path={title} /> : undefined}
                    value={tab}
                    onChange={event => handleChange(event, event.target.value)}
                >
                    {React.Children.map(children, (child, index) => {
                        const { tab, tabMenu, label, TabProps } = child ? child.props || {} : {};
                        const tabErrors = _getErrorsForTab(tab);
                        if (!tab && !tabMenu) {
                            return null;
                        }

                        return (
                            <MenuItem key={tab || index} value={tab} onClick={TabProps?.onClick}>
                                <LocalizedTooltip
                                    title={tabErrors}
                                    delimiter={`\n`}
                                    classes={classes}
                                    placement="bottom-start"
                                >
                                    <VBox>
                                        {tab ? <Locale path={label} /> : child}
                                        {tabErrors ? (
                                            <ErrorIcon fontSize="small" color="danger" />
                                        ) : null}
                                    </VBox>
                                </LocalizedTooltip>
                            </MenuItem>
                        );
                    })}
                </TextField>
            ) : (
                <Tabs
                    {...remain}
                    className={
                        orientation === 'vertical' ? classes.verticalTabs : classes.horizontalTabs
                    }
                    color={orientation === 'vertical' ? 'transparent' : undefined}
                    elevation={orientation === 'vertical' ? 0 : undefined}
                    orientation={orientation}
                    variant={orientation === 'vertical' ? 'standard' : 'scrollable'}
                    textColor={orientation === 'vertical' ? 'secondary' : undefined}
                    value={tab}
                    onChange={handleChange}
                    scrollButtons="auto"
                >
                    {React.Children.map(children, child => {
                        const {
                            TabProps,
                            tab: panelTab,
                            tabMenu,
                            label,
                            notificationCount = 0,
                            notificationBadgeVariant = 'primary',
                        } = child ? child.props || {} : {};
                        const tabErrors = _getErrorsForTab(panelTab);
                        return panelTab ? (
                            <Tab
                                label={
                                    <LocalizedTooltip
                                        title={tabErrors}
                                        delimiter={`\n`}
                                        classes={classes}
                                        placement="bottom-start"
                                    >
                                        <VBox>
                                            <Locale path={label} />
                                            <CountBadge
                                                className={classes.notificationCount}
                                                count={notificationCount}
                                                variant={notificationBadgeVariant}
                                            />
                                            {tabErrors ? (
                                                <ErrorIcon fontSize="small" color="danger" />
                                            ) : null}
                                        </VBox>
                                    </LocalizedTooltip>
                                }
                                className={
                                    orientation === 'vertical' ? classes.tabVertical : classes.tab
                                }
                                value={panelTab}
                                disableRipple
                                disableFocusRipple
                                name={name}
                                {...TabProps}
                            />
                        ) : tabMenu ? (
                            child
                        ) : null;
                    })}
                </Tabs>
            )}
            <ContentWrap className={classes.panels}>
                {error ? (
                    <ErrorOverlayComponent error={error} {...props.slotProps?.errorOverlay} />
                ) : (
                    React.Children.map(children, child => {
                        const {
                            TabPanelProps,
                            tab: panelTab,
                            tabMenu,
                        } = child ? child.props || {} : {};
                        return panelTab ? (
                            panelTab === tab && !tabMenu ? (
                                <TabPanel
                                    key={panelTab}
                                    selected={tab}
                                    tab={panelTab}
                                    name={name}
                                    className={classes.panel}
                                    {...TabPanelProps}
                                >
                                    {child}
                                </TabPanel>
                            ) : null
                        ) : tabMenu ? null : (
                            child
                        );
                    })
                )}
            </ContentWrap>
        </div>
    );
};

DynamicTabbed.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    ContentWrap: PropTypes.elementType,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Error)]),
    errorTabs: PropTypes.array,
    initialTab: PropTypes.string,
    name: PropTypes.string,
    onChange: PropTypes.func,
    orientation: PropTypes.oneOf(['horizontal', 'vertical']),
    preventScrollOnChange: PropTypes.bool,
    shouldAutoUpdateUrl: PropTypes.bool,
    slots: PropTypes.shape({
        errorOverlay: PropTypes.node,
    }),
    slotProps: PropTypes.shape({
        errorOverlay: PropTypes.object,
    }),
    tab: PropTypes.string,
    title: PropTypes.string,
};

DynamicTabbed.defaultProps = {
    ContentWrap: Box,
};

export default DynamicTabbed;
